import { useAppointmentModuleSearchStore } from 'src/stores/appointmentModuleSearch';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';

import { registerListInstanceFetcher } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const fetchAgendaList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useAppointmentModuleSearchStore> = useAppointmentModuleSearchStore()
) => {
	const fetchPayload = {
		clearResultsBeforeFetch: queryParameters.clearResultsBeforeFetch,
		hiddenSearchTerm: queryParameters.hiddenSearchTerm,
		queryStore: queryStore,
		searchTerm: queryParameters.searchTerm,
	};

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		void listStore.fetchMineResults(fetchPayload);
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		void listStore.fetchAllResults(fetchPayload);
	}
};

registerListInstanceFetcher('agendaList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchAgendaList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.8 (analog Task 2.6/2.7): Basis-Identifier des Sub-Listen-Scrollers — muss Agenda.vue's
// `virtual-list-id="moduleActivityListScrollerAgenda"` spiegeln (dort mit Mine/All-Suffix versehen,
// s. GenericActivityListTabPanel.vue). Kein zentraler Export vorhanden, daher hier dieselbe
// Literal-Konstante wie in Agenda.vue.
const AGENDA_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerAgenda';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs sind für agendaList
// nicht erreichbar (kein Eintrag in SharedQueryRouteName/DesktopSharedQueryRouteName, s. Report) und
// disposen ohnehin vollständig über useListInstanceLifecycle. Reduzierte Shape (analog Task 2.7 inbox):
// appointmentModuleSearch trägt KEINE Box-/Benutzer-Filter, KEIN dateFilter, KEINEN Richtungsfilter
// und KEINE Shared Query (Store-State s. state.ts) — nur dateTimeRangeFilter, Such-/Hidden-Suchbegriff
// (auf filterStore, MODULE.AGENDA_LIST-keyed), Mehrfachauswahl, Scroll-Position, aktive Ansicht.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination.
const resetAgendaListModuleTabSettings = (): void => {
	const appointmentModuleSearchStore = useAppointmentModuleSearchStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	appointmentModuleSearchStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.ALL });

	filterStore.setSearchTerm({ module: 'agendaList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'agendaList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'agendaList' });
	genericActivityListStore.resetScrollPositions(AGENDA_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({
		activeView: MODULE_LIST_TYPE.OPEN,
		genericListIdentifier: 'agendaList',
	});
};

registerModuleTabSettingsReset('agendaList', resetAgendaListModuleTabSettings);

export { fetchAgendaList };
